import {
  escapeAttribute,
  HREF,
  readAttribute,
  SAFE_HREF,
  stripRawContent,
} from './html-sanitize-core';

const ALLOWED_TAGS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'em',
  's',
  'a',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'code',
  'br',
  'hr',
]);

const MENTION_TYPE = /\bdata-type\s*=\s*(?:"mention"|'mention'|mention\b)/i;
const MENTION_ID = /\bdata-id\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;
const MENTION_LABEL = /\bdata-label\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;
/** Mention ids address a user row, so anything but an id is a forgery. */
const MENTION_ID_VALUE = /^\d+$/;
/** Mirrors the Tiptap `Mention` extension's `HTMLAttributes.class`. */
const MENTION_CLASS = 'mention text-primary font-semibold';

/**
 * `<span>` survives only as an @mention, which the comment editor emits and the
 * backend reads back with `extractMentions`. The tag is rebuilt from scratch —
 * fixed class, id checked, label stripped of angle brackets — so no stored
 * attribute reaches the DOM and re-sanitizing the result is a no-op.
 */
function mentionOpenTag(attributes: string) {
  if (!MENTION_TYPE.test(attributes)) return null;

  const id = readAttribute(attributes, MENTION_ID);
  if (!MENTION_ID_VALUE.test(id)) return null;

  const label = escapeAttribute(readAttribute(attributes, MENTION_LABEL).replace(/[<>]/g, ''));
  return `<span class="${MENTION_CLASS}" data-type="mention" data-id="${id}" data-label="${label}">`;
}

/**
 * Keeps the small HTML vocabulary emitted by the Tiptap editor and drops all
 * attributes except safe links. This is intentionally DOM-free for Workers.
 *
 * Applied on write *and* on render: stored rows predate this sanitizer, so the
 * render path cannot assume the database is clean.
 */
export function sanitizeRichText(html: string) {
  const stripped = stripRawContent(html);

  // Spans are kept selectively (mentions only), so their closing tags cannot be
  // rewritten in isolation — this tracks which open span each `</span>` closes.
  const keptSpans: boolean[] = [];

  return stripped.replace(
    /<\/?\s*([a-z][\w-]*)([^>]*)>/gi,
    (raw, rawTag: string, attributes: string) => {
      const tag = rawTag.toLowerCase();
      const isClosing = raw.trimStart().startsWith('</');

      if (tag === 'span') {
        if (isClosing) return keptSpans.pop() ? '</span>' : '';
        // A self-closing span never gets a matching `</span>`, so it stays off
        // the stack — and a childless mention has nothing to render anyway.
        if (attributes.trimEnd().endsWith('/')) return '';
        const mention = mentionOpenTag(attributes);
        keptSpans.push(mention !== null);
        return mention ?? '';
      }

      if (!ALLOWED_TAGS.has(tag)) return '';
      if (isClosing) return `</${tag}>`;
      if (tag !== 'a') return `<${tag}>`;

      const href = readAttribute(attributes, HREF);
      return href && SAFE_HREF.test(href) ? `<a href="${escapeAttribute(href)}">` : '<a>';
    }
  );
}

/**
 * `sanitizeRichText` for nullable columns: preserves `null`/`undefined` so
 * callers can keep "clear the field" and "leave it alone" distinct.
 */
export function sanitizeOptionalRichText<T extends string | null | undefined>(value: T): T {
  return (typeof value === 'string' ? sanitizeRichText(value) : value) as T;
}

export function richTextPlainText(html: string) {
  return sanitizeRichText(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(?:nbsp|#160);/gi, ' ')
    .replace(/&(?:amp|#38);/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Prepares a stored value for the rich-text editor and renderer.
 *
 * A task description written before descriptions were rich text is plain text whose newlines
 * carried its structure — the old renderers used `whitespace-pre-wrap` to show them. Rendering
 * that same string as HTML would collapse every break into one paragraph, which is a visible
 * regression on real rows. So a tag-free value is converted to paragraphs here, and anything
 * already containing markup is passed through untouched.
 */
export function ensureRichText(value: string | null | undefined): string {
  if (!value) return '';
  // A tag anywhere means this was written by the editor and already carries its own structure.
  if (/<[a-z][\s\S]*>/i.test(value)) return value;

  const escapeHtml = (text: string) =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return value
    .split(/\r?\n/)
    .map((line) => `<p>${line.trim() === '' ? '<br>' : escapeHtml(line)}</p>`)
    .join('');
}
